/**
 * CheckpointSummaryPanel - AI-powered project checkpoint summary
 * Displays KPI status, at-risk criteria, drift notes, and exec summary
 * 
 * Feature flag: features.ai.checkpoints
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart3, AlertTriangle, CheckCircle, Target, TrendingDown,
  TrendingUp, Clock, RefreshCw, ChevronDown, ChevronUp,
  FileText, Download, Loader2, Info, AlertCircle
} from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useProductMode } from '../context/ProductModeContext';
import { trackEvent, TELEMETRY_EVENTS } from '../utils/telemetry';
import { toast } from 'sonner';

// Status colors for visual indicators
const STATUS_COLORS = {
  met: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
  'at-risk': { bg: 'bg-orange-100', text: 'text-orange-700', icon: AlertTriangle },
  'not-met': { bg: 'bg-red-100', text: 'text-red-700', icon: AlertCircle },
  proposed: { bg: 'bg-gray-100', text: 'text-gray-600', icon: Clock },
  accepted: { bg: 'bg-blue-100', text: 'text-blue-700', icon: CheckCircle },
};

/**
 * KPI Status Card
 */
const KPIStatusCard = ({ kpi }) => {
  const status = STATUS_COLORS[kpi.status] || STATUS_COLORS.proposed;
  const StatusIcon = status.icon;
  
  return (
    <div 
      className="p-3 bg-white rounded-lg border border-gray-100 hover:border-gray-200 transition-colors"
      data-testid={`kpi-status-${kpi.id}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Target className="w-4 h-4 text-purple-500 flex-shrink-0" />
            <h4 className="font-medium text-sm text-gray-800 truncate">{kpi.name}</h4>
          </div>
          
          <div className="flex items-center gap-2 mt-2">
            {kpi.currentValue !== undefined && kpi.currentValue !== null && (
              <span className="text-xs text-gray-600">
                Current: <strong>{kpi.currentValue}</strong>
              </span>
            )}
            {kpi.variance !== undefined && kpi.variance !== null && (
              <span className={`text-xs flex items-center gap-0.5 ${
                kpi.variance >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {kpi.variance >= 0 ? (
                  <TrendingUp className="w-3 h-3" />
                ) : (
                  <TrendingDown className="w-3 h-3" />
                )}
                {kpi.variance > 0 ? '+' : ''}{kpi.variance}%
              </span>
            )}
          </div>
        </div>
        
        <div className={`flex-shrink-0 px-2 py-1 rounded-full flex items-center gap-1 ${status.bg} ${status.text}`}>
          <StatusIcon className="w-3 h-3" />
          <span className="text-[10px] font-medium capitalize">{kpi.status}</span>
        </div>
      </div>
    </div>
  );
};

/**
 * At-Risk Item Card
 */
const AtRiskItemCard = ({ item }) => {
  const typeIcons = {
    acceptance: CheckCircle,
    kpi: Target,
    drift: AlertTriangle,
  };
  const TypeIcon = typeIcons[item.type] || AlertTriangle;
  
  return (
    <div 
      className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-100"
      data-testid={`at-risk-item-${item.id || item.name}`}
    >
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
        <TypeIcon className="w-4 h-4 text-red-600" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-[10px] font-medium text-red-600 uppercase">{item.type}</span>
          {item.severity && (
            <span className="text-[10px] px-1.5 py-0.5 bg-red-200 text-red-700 rounded">
              {item.severity}
            </span>
          )}
        </div>
        <p className="text-sm text-gray-800 line-clamp-2">{item.name || item.text}</p>
        <p className="text-xs text-gray-500 mt-1 capitalize">{item.status}</p>
      </div>
    </div>
  );
};

/**
 * Main CheckpointSummaryPanel Component
 */
const CheckpointSummaryPanel = ({
  projectId,
  className = '',
  variant = 'full', // 'full' | 'compact' | 'card'
  defaultExpanded = true
}) => {
  const { user } = useAuth();
  const { getFeature, scopingTier } = useProductMode();
  
  const tenantId = user?.tenant?.id || user?.tenant_id;
  
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [window, setWindow] = useState(30); // 30 or 90 days
  const [lastFetched, setLastFetched] = useState(null);
  
  // Check if feature is enabled
  const isEnabled = getFeature('ai.checkpoints', false);
  
  // Fetch checkpoint summary
  const fetchSummary = useCallback(async () => {
    if (!projectId || !tenantId || !isEnabled) return;
    
    setLoading(true);
    setError(null);
    
    trackEvent(TELEMETRY_EVENTS.AI_CHECKPOINT_SUMMARY_REQUESTED, { 
      projectId, 
      window 
    });
    
    try {
      const response = await api.getCheckpointSummary(tenantId, {
        projectId,
        includeRisks: true,
        includeDrift: true
      });
      
      setData(response.data);
      setLastFetched(new Date());
      
      trackEvent(TELEMETRY_EVENTS.AI_CHECKPOINT_SUMMARY_VIEWED, { projectId });
    } catch (err) {
      console.error('Failed to fetch checkpoint summary:', err);
      setError(err.response?.data?.detail || 'Failed to generate summary');
      toast.error('Failed to load checkpoint summary');
    } finally {
      setLoading(false);
    }
  }, [projectId, tenantId, window, isEnabled]);
  
  // Initial load
  useEffect(() => {
    if (projectId && tenantId && isEnabled && !data) {
      fetchSummary();
    }
  }, [projectId, tenantId, isEnabled]);
  
  // Feature not enabled
  if (!isEnabled) {
    return null;
  }
  
  // Compact variant - just a button to expand
  if (variant === 'compact' && !expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className={`flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors ${className}`}
        data-testid="checkpoint-summary-expand-btn"
      >
        <BarChart3 className="w-4 h-4" />
        <span className="text-sm font-medium">View Checkpoint Summary</span>
      </button>
    );
  }
  
  // Empty state
  const renderEmptyState = () => (
    <div className="text-center py-8">
      <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
        <BarChart3 className="w-6 h-6 text-gray-400" />
      </div>
      <p className="text-sm text-gray-600 mb-1">No checkpoint summary yet</p>
      <p className="text-xs text-gray-500 mb-4">
        Run a summary to assess risks and KPI status.
      </p>
      <button
        onClick={fetchSummary}
        disabled={loading}
        className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
        data-testid="generate-summary-btn"
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <BarChart3 className="w-4 h-4" />
        )}
        Generate Summary
      </button>
    </div>
  );
  
  // Error state
  const renderErrorState = () => (
    <div className="text-center py-8">
      <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-red-100 flex items-center justify-center">
        <AlertTriangle className="w-6 h-6 text-red-500" />
      </div>
      <p className="text-sm text-red-600 mb-4">{error}</p>
      <button
        onClick={fetchSummary}
        disabled={loading}
        className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-50"
      >
        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        Retry
      </button>
    </div>
  );
  
  // Calculate summary stats
  const acceptanceAtRisk = data?.acceptanceStatus?.filter(a => a.atRisk)?.length || 0;
  const acceptanceTotal = data?.acceptanceStatus?.length || 0;
  const kpisAtRisk = data?.kpiStatus?.filter(k => k.status === 'at-risk' || k.status === 'not-met')?.length || 0;
  const kpisTotal = data?.kpiStatus?.length || 0;
  
  return (
    <div 
      className={`bg-gradient-to-br from-white to-gray-50 rounded-xl border border-gray-200 shadow-sm overflow-hidden ${className}`}
      data-testid="checkpoint-summary-panel"
    >
      {/* Header */}
      <div 
        className="flex items-center justify-between p-4 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
        role="button"
        aria-expanded={expanded}
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Checkpoint Summary</h3>
            <p className="text-xs text-gray-500">
              {data ? (
                <>
                  {acceptanceAtRisk > 0 && (
                    <span className="text-orange-600">{acceptanceAtRisk} at-risk criteria</span>
                  )}
                  {acceptanceAtRisk > 0 && kpisAtRisk > 0 && ' · '}
                  {kpisAtRisk > 0 && (
                    <span className="text-orange-600">{kpisAtRisk} KPIs need attention</span>
                  )}
                  {acceptanceAtRisk === 0 && kpisAtRisk === 0 && (
                    <span className="text-green-600">All items on track</span>
                  )}
                </>
              ) : 'AI-powered project health assessment'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {loading && <Loader2 className="w-4 h-4 text-purple-500 animate-spin" />}
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </div>
      
      {/* Content */}
      {expanded && (
        <div className="px-4 pb-4">
          {/* Window selector */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
              <button
                onClick={() => setWindow(30)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  window === 30 ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                30 Days
              </button>
              <button
                onClick={() => setWindow(90)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  window === 90 ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                90 Days
              </button>
            </div>
            
            <button
              onClick={fetchSummary}
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
              data-testid="refresh-summary-btn"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
          
          {/* Loading state */}
          {loading && !data && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Loader2 className="w-8 h-8 text-purple-500 animate-spin mx-auto mb-3" />
                <p className="text-sm text-gray-600">Generating checkpoint summary...</p>
                <p className="text-xs text-gray-400 mt-1">This may take a few seconds</p>
              </div>
            </div>
          )}
          
          {/* Error state */}
          {error && !loading && renderErrorState()}
          
          {/* Empty state */}
          {!data && !loading && !error && renderEmptyState()}
          
          {/* Data display */}
          {data && !loading && (
            <div className="space-y-4">
              {/* Executive Summary */}
              {data.summary && (
                <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-100">
                  <div className="flex items-start gap-2 mb-2">
                    <FileText className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                    <h4 className="text-sm font-medium text-purple-900">Executive Summary</h4>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {data.summary}
                  </p>
                </div>
              )}
              
              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-white rounded-lg border border-gray-100">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle className="w-4 h-4 text-blue-500" />
                    <span className="text-xs font-medium text-gray-600">Acceptance Criteria</span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-gray-900">
                      {acceptanceTotal - acceptanceAtRisk}
                    </span>
                    <span className="text-sm text-gray-500">/ {acceptanceTotal} on track</span>
                  </div>
                  {acceptanceAtRisk > 0 && (
                    <span className="text-xs text-orange-600 mt-1">
                      {acceptanceAtRisk} at risk
                    </span>
                  )}
                </div>
                
                <div className="p-3 bg-white rounded-lg border border-gray-100">
                  <div className="flex items-center gap-2 mb-1">
                    <Target className="w-4 h-4 text-purple-500" />
                    <span className="text-xs font-medium text-gray-600">KPIs</span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-gray-900">
                      {kpisTotal - kpisAtRisk}
                    </span>
                    <span className="text-sm text-gray-500">/ {kpisTotal} met</span>
                  </div>
                  {kpisAtRisk > 0 && (
                    <span className="text-xs text-orange-600 mt-1">
                      {kpisAtRisk} need attention
                    </span>
                  )}
                </div>
              </div>
              
              {/* At-Risk Items */}
              {data.atRiskItems && data.atRiskItems.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-orange-500" />
                    At-Risk Items ({data.atRiskItems.length})
                  </h4>
                  <div className="space-y-2">
                    {data.atRiskItems.slice(0, 5).map((item, idx) => (
                      <AtRiskItemCard key={item.id || idx} item={item} />
                    ))}
                    {data.atRiskItems.length > 5 && (
                      <p className="text-xs text-gray-500 text-center py-2">
                        +{data.atRiskItems.length - 5} more items
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              {/* KPI Status */}
              {data.kpiStatus && data.kpiStatus.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <Target className="w-4 h-4 text-purple-500" />
                    KPI Status
                  </h4>
                  <div className="space-y-2">
                    {data.kpiStatus.map((kpi, idx) => (
                      <KPIStatusCard key={kpi.id || idx} kpi={kpi} />
                    ))}
                  </div>
                </div>
              )}
              
              {/* Drift Impacts */}
              {data.driftImpacts && data.driftImpacts.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <TrendingDown className="w-4 h-4 text-red-500" />
                    Drift Notes
                  </h4>
                  <div className="space-y-2">
                    {data.driftImpacts.slice(0, 3).map((drift, idx) => (
                      <div 
                        key={idx}
                        className="p-3 bg-orange-50 rounded-lg border border-orange-100"
                      >
                        <p className="text-sm text-gray-700">{drift.reason || drift.note}</p>
                        {drift.severity && (
                          <span className={`inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded ${
                            drift.severity === 'high' ? 'bg-red-100 text-red-700' :
                            drift.severity === 'medium' ? 'bg-orange-100 text-orange-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {drift.severity}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Export button placeholder */}
              <div className="pt-3 border-t border-gray-100">
                <button
                  disabled
                  className="w-full flex items-center justify-center gap-2 py-2 text-sm text-gray-400 bg-gray-50 rounded-lg cursor-not-allowed"
                  data-testid="export-summary-btn"
                >
                  <Download className="w-4 h-4" />
                  Export coming soon
                </button>
                {lastFetched && (
                  <p className="text-[10px] text-gray-400 text-center mt-2">
                    Last updated: {lastFetched.toLocaleTimeString()}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CheckpointSummaryPanel;
